<template>
  <section :class="contentClasses">
     <div class="row">
      <div class="col-12">
        <!-- <search-scope-select-bar v-if="useCombinedSearch" :config="searchFiltersConfig" /> -->
        <search-scope-select-bar v-if="shouldShowSearchScope" :config="searchFiltersConfig" />
      </div>
    </div>
    <search-filters
      :config="searchFiltersConfig"
    />
    <datasets-info-box />
    <selectedFacetsOverview
      v-if="sanitizedSelectedFacets"
      :selected-facets="sanitizedSelectedFacets"
      :available-facets="normalizedAvailableFacets"
    >
      <template #default="{ index, facet, facetId, findFacetTitle, removeSelectedFacet }">
        <span
          :key="index"
          class="badge badge-pill badge-highlight mr-1 ds-label"
        >
          {{ resolveSelectedFacetLabel(facet, facetId, findFacetTitle) }}
          <span
            @click="removeSelectedFacet(facet.field, facetId)"
            class="close-facet ml-2"
          >
            ×
          </span>
        </span>
      </template>
    </selectedFacetsOverview>
    <dataset-details-skeleton v-if="getLoading" type="Datasets" />
    <template v-if="!getLoading">
      <dataset-list-infos />
      <datasets-list :dss-id="dssId" />
    </template>
  </section>
</template>

<script>

import {
  DatasetDetailsSkeleton,
  SelectedFacetsOverview,
} from "@piveau/piveau-hub-ui-modules";
import SearchFilters from "@/components/search/search-filters/SearchFilters";
import DatasetsList from "@/components/search/datasets/DatasetsList";
import DatasetListInfos from "@/components/search/datasets/DatasetListInfos";
import DatasetsInfoBox from "@/components/search/datasets/DatasetsInfoBox";
import {mapGetters} from "vuex";
import SearchScopeSelectBar from "@/components/search/search-filters/SearchScopeSelectBar";

export default {
  name: "DatasetsContent",
  components: {
    SearchScopeSelectBar,
    SearchFilters,
    DatasetDetailsSkeleton,
    SelectedFacetsOverview,
    DatasetsList,
    DatasetListInfos,
    DatasetsInfoBox,
  },props:{
     inSeriesDetailsPage: {
      type: Boolean,
      default: false,
      required: false
    },
    dssId: {
      type: String,
      default: null,
      required: false
    }
  },
  data() {
    return {
      searchFiltersConfig: {
        scope: "dataset",
        apiLinkIncludes: `id,title.${this.$route.query.locale},description.${this.$route.query.locale},languages,modified,
        issued,catalog.id,catalog.title,catalog.country.id,distributions.id,distributions.format.label,
        distributions.format.id,distributions.license,categories.label,publisher`,
        textFieldHelpLink: `https://dataeuropa.gitlab.io/data-provider-manual/how-to-search/search-for-datasets/`,
        textKeys: {
          mobileSearchButton: 'message.datasets.ecd.filters.search',
          textFieldPlaceholder: 'message.datasets.searchBar.placeholder',
          count: "message.datasets.countMessage",
          loading: "message.datasets.loadingMessage"
        },
        getQuery: ["datasets", "getQuery"],
        setQuery: ["datasets", "setQuery"],
        setSort: ["datasets", "setSort"],
        getCount: ["datasets", "getDatasetsCount"],
        getLoading: ["datasets", "getLoading"],
        getFacets: ["datasets", "getFacets"],
      },
      useCombinedSearch: this.$env.content.useCombinedSearch
    }
  },
  computed: {
    ...mapGetters("datasets", ["getFacets", "getQuery", "getAllAvailableFacets",
      "getLoading", "getDatasetsCount"]),
    ...mapGetters("search", ["getShowFilter"]),
    sanitizedSelectedFacets() {
      if (!this.getFacets || !this.getAllAvailableFacets) return this.getFacets;

      const availableByField = this.getAllAvailableFacets.reduce((acc, facet) => {
        const ids = new Set((facet?.items || []).map((item) => String(item.id)));
        acc[facet.id] = ids;
        return acc;
      }, {});

      return Object.entries(this.getFacets).reduce((acc, [field, values]) => {
        const selectedValues = Array.isArray(values) ? values : [values];
        const availableIds = availableByField[field];

        if (!availableIds) {
          acc[field] = selectedValues;
          return acc;
        }

        acc[field] = selectedValues.filter((value) =>
          availableIds.has(String(value))
        );
        return acc;
      }, {});
    },
    normalizedAvailableFacets() {
      const locale = this.$route.query.locale || this.$i18n.locale || "en";
      const activeDataScopeFacetId = this.$env?.content?.datasets?.facets?.dataScopeFacetId || "dataScope";
      const facets = Array.isArray(this.getAllAvailableFacets)
        ? this.getAllAvailableFacets
        : [];

      const byId = new Map();
      facets.forEach((facet) => {
        if (!facet || !facet.id) return;
        const normalizedId = (facet.id === "dataScope" || facet.id === "corporate-body-classification")
          ? activeDataScopeFacetId
          : facet.id;
        const items = (Array.isArray(facet.items) ? facet.items : []).map((item) => {
          const rawTitle = item?.title;
          const titleMap =
            rawTitle && typeof rawTitle === "object" && rawTitle.title && typeof rawTitle.title === "object"
              ? rawTitle.title
              : rawTitle;
          const normalizedTitle =
            titleMap && typeof titleMap === "object"
              ? titleMap[locale] || titleMap.en || Object.values(titleMap).find((v) => typeof v === "string") || ""
              : rawTitle;
          return {
            ...item,
            title: normalizedTitle,
          };
        });

        const existing = byId.get(normalizedId);
        if (!existing) {
          byId.set(normalizedId, {
            ...facet,
            id: normalizedId,
            items,
          });
          return;
        }

        const existingItems = Array.isArray(existing.items) ? existing.items : [];
        const merged = new Map(existingItems.map((item) => [String(item.id), item]));
        items.forEach((item) => {
          if (!merged.has(String(item.id))) merged.set(String(item.id), item);
        });
        existing.items = Array.from(merged.values());
      });

      return Array.from(byId.values());
    },
    shouldShowSearchScope() {
      if (this.$route.meta.hideSearchScope) return false;
      if (this.inSeriesDetailsPage) return false;
      return this.useCombinedSearch;
    },
     contentClasses() {
      // Full width when facets are hidden or not in use
      if (!this.getShowFilter) {
         return 'col-12 px-lg-0 px-sm-2'; 
      }
      // Default classes when facets are shown
      return 'col-md-9 col-12';
    }
  }
  ,
  methods: {
    resolveSelectedFacetLabel(facet, facetId, findFacetTitle) {
      if (facet?.field === "country" && String(facetId).toLowerCase() === "eu") {
        return "Europe";
      }

      const label = findFacetTitle(facet.field, facetId);
      if (typeof label === "string" && label.trim().length > 0) {
        return label;
      }

      const fallbackFacet = this.normalizedAvailableFacets.find(
        (availableFacet) => availableFacet.id === facet?.field
      );
      const fallbackItem = fallbackFacet?.items?.find(
        (item) => String(item.id) === String(facetId)
      );

      if (
        fallbackItem?.title &&
        String(fallbackItem.title).trim().length > 0
      ) {
        return fallbackItem.title;
      }

      return String(facetId);
    },
  },
}
</script>

<style lang="scss" scoped>
.close-facet {
  cursor: pointer;
}
</style>
